import { useEffect } from 'react';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * Synth-based triumphant fanfare using the Web Audio API — no asset
 * download, works offline. Plays an ascending arpeggio ending on a
 * sustained major chord, so it sounds like an actual celebration rather
 * than a notification blip.
 */
const playGoalFanfare = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    if (ctx.state === 'suspended') ctx.resume();

    const now = ctx.currentTime;
    // C major run-up → hold a C+E+G+C chord for the "arrival"
    const run = [523.25, 659.25, 783.99, 1046.5]; // C5 E5 G5 C6
    run.forEach((freq, i) => {
      const start = now + i * 0.11;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, start);
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.22, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.55);
    });

    // Sustained final chord for the "you did it" feeling
    const chordStart = now + run.length * 0.11 + 0.05;
    [523.25, 659.25, 783.99, 1046.5].forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, chordStart);
      gain.gain.setValueAtTime(0, chordStart);
      gain.gain.linearRampToValueAtTime(0.12, chordStart + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, chordStart + 1.6);
      osc.connect(gain).connect(ctx.destination);
      osc.start(chordStart);
      osc.stop(chordStart + 1.7);
    });

    setTimeout(() => ctx.close().catch(() => {}), 2400);
  } catch {
    // Best-effort — silent fail on unsupported browsers.
  }
};

/**
 * Full-screen celebration shown the moment a user completes their final
 * workout of the week. Uses the Ascend & Surpass rising sun as the hero
 * graphic and keeps text on a solid backing for readability.
 */
export const WeeklyGoalCelebrationModal = ({ isOpen, onClose, weeklyGoal, workoutTitle }) => {
  useEffect(() => {
    if (isOpen) playGoalFanfare();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md bg-black border-primary overflow-hidden p-0"
        data-testid="weekly-goal-celebration-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center"
          data-testid="celebration-close-btn"
          aria-label="close"
        >
          <X className="h-5 w-5 text-white" />
        </button>

        {/* Radial glow behind the sun */}
        <div className="relative bg-gradient-to-b from-amber-500/20 via-black to-black px-6 pt-10 pb-8">
          <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_30%,rgba(250,204,21,0.35),transparent_60%)]" />

          <motion.img
            src="/badges/rising-sun.png"
            alt="Ascend & Surpass rising sun"
            initial={{ scale: 0.6, rotate: -12, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18 }}
            className="relative mx-auto w-56 h-56 object-contain drop-shadow-[0_0_35px_rgba(250,204,21,0.6)]"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
            className="relative text-center mt-4"
          >
            <h2 className="font-heading text-4xl text-white uppercase tracking-wider drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
              Goal Crushed!
            </h2>
            <p className="mt-3 text-primary text-xl font-semibold drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
              {weeklyGoal} workouts this week — you did it.
            </p>
            {workoutTitle && (
              <p className="mt-2 text-white/85 text-base drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                Final workout: <span className="font-semibold">{workoutTitle}</span>
              </p>
            )}

            <Button
              onClick={onClose}
              className="mt-6 bg-primary text-black hover:bg-primary/90 font-heading tracking-wider uppercase px-8 py-2.5 text-base"
              data-testid="celebration-dismiss-btn"
            >
              Keep Pushing
            </Button>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
